import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import get from 'lodash/get';
import { Row } from '../../../../CommonComponents/OPL/Grid/Row';
import { Column } from '../../../../CommonComponents/OPL/Grid/Column';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { DraftAccountDTO, yesNoOpts } from '../../AccountOpening.meta';
import { SpaceProps } from '../../../../theme/styledProps';
import { Input } from '../../../../CommonComponents/OPL/Input/Input';
import { relationshipOpts } from './Beneficiary.meta';
import { InputMask } from '../../../../CommonComponents/OPL/Input/InputMask';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import Grid from '../../../../CommonComponents/Grid';
import { EditableField } from '../../components/EditableField/EditableField';
import { ABBREVIATED_STATES_US } from '../../../../CommonConstants';

const fieldName = 'retirementAccountBeneficiaryInfo';

const hasBeneficiaryFieldName = `${fieldName}.hasBeneficiary`;
const relationshipFieldName = `${fieldName}.relationship`;
const firstNameFieldName = `${fieldName}.givenName`;
const lastNameFieldName = `${fieldName}.familyName`;
const dobFieldName = `${fieldName}.dateOfBirth`;
const ssnFieldName = `${fieldName}.socialSecurityNumber`;
const addressFieldName = `${fieldName}.streetAddress`;
const cityFieldName = `${fieldName}.city`;
const stateFieldName = `${fieldName}.state`;
const postalCodeFieldName = `${fieldName}.postalCode`;

interface Props extends SpaceProps {
  isReview?: boolean;
  draftAccount: DraftAccountDTO;
}

export const Beneficiary: React.FC<Props> = ({ isReview, draftAccount, ...rest }) => {
  const formBag = useFormContext();
  const {
    formState: { errors },
  } = formBag;

  const [isEditing, setIsEditing] = useState(!isReview);

  const hasBeneficiary = formBag.watch(hasBeneficiaryFieldName);

  if (!isEditing) {
    return (
      <EditableField onEdit={() => setIsEditing(true)}>
        <Text fontWeight={500}>{draftAccount[fieldName] ? 'Yes' : 'No'}</Text>

        {draftAccount[fieldName] && (
          <Text fontWeight={500}>
            {draftAccount.retirementAccountBeneficiaryInfo?.givenName}{' '}
            {draftAccount.retirementAccountBeneficiaryInfo?.familyName}
          </Text>
        )}
      </EditableField>
    );
  }

  return (
    <Box {...rest}>
      <Controller
        control={formBag.control}
        name={hasBeneficiaryFieldName}
        render={({ field }) => (
          <Select
            value={yesNoOpts.find(({ value }) => value === field.value)}
            label={isReview ? undefined : 'Would you like to add a beneficiary for this account?'}
            onChange={opt => {
              if (opt) {
                formBag.setValue(hasBeneficiaryFieldName, opt.value);
              }
            }}
            options={yesNoOpts}
            aria-label="Would you like to add a beneficiary for this account?"
          />
        )}
      />

      {hasBeneficiary && (
        <>
          <Box mt={isReview ? '8' : '32'}>
            <Controller
              control={formBag.control}
              name={relationshipFieldName}
              render={({ field }) => (
                <Select
                  innerRef={field.ref}
                  value={relationshipOpts.find(({ value }) => value === field.value)}
                  label="Relationship"
                  onChange={opt => {
                    if (opt) {
                      formBag.setValue(relationshipFieldName, opt.value, {
                        shouldValidate: true,
                      });
                    }
                  }}
                  options={relationshipOpts}
                  formVariant={get(errors, relationshipFieldName) ? 'error' : undefined}
                  helperText={get(errors, relationshipFieldName)?.message}
                />
              )}
            />
          </Box>

          <Grid>
            <Row mt={isReview ? '8' : '32'}>
              <Column width={6}>
                <Input
                  {...formBag.register(firstNameFieldName)}
                  label="First name"
                  type="text"
                  placeholder="First name"
                  formVariant={get(errors, firstNameFieldName) ? 'error' : undefined}
                  helperText={get(errors, firstNameFieldName)?.message}
                  autoComplete="given-name"
                  maxLength={256}
                />
              </Column>

              <Column width={6}>
                <Input
                  {...formBag.register(lastNameFieldName)}
                  label="Last name"
                  type="text"
                  placeholder="Last name"
                  formVariant={get(errors, lastNameFieldName) ? 'error' : undefined}
                  helperText={get(errors, lastNameFieldName)?.message}
                  autoComplete="family-name"
                  maxLength={256}
                />
              </Column>
            </Row>

            <Row mt={isReview ? '8' : '32'}>
              <Column width={6}>
                <InputMask
                  {...formBag.register(dobFieldName)}
                  label="Date of Birth"
                  type="text"
                  mask="99/99/9999"
                  placeholder="MM/DD/YYYY"
                  leftIcon="far fa-calendar-alt"
                  formVariant={get(errors, dobFieldName) ? 'error' : undefined}
                  helperText={get(errors, dobFieldName)?.message}
                  autoComplete="bday"
                />
              </Column>

              <Column width={6}>
                <InputMask
                  {...formBag.register(ssnFieldName)}
                  label="SSN"
                  type="text"
                  mask="999-99-9999"
                  placeholder="XXX - XX - XXXX"
                  leftIcon="far fa-address-card"
                  formVariant={get(errors, ssnFieldName) ? 'error' : undefined}
                  helperText={get(errors, ssnFieldName)?.message}
                />
              </Column>
            </Row>

            <Text mt={isReview ? '8' : '32'} mb="4" variant="formLabel">
              Primary Address
            </Text>

            <Row>
              <Column width={12}>
                <Input
                  {...formBag.register(addressFieldName)}
                  type="text"
                  placeholder="Address Line"
                  formVariant={get(errors, addressFieldName) ? 'error' : undefined}
                  helperText={get(errors, addressFieldName)?.message}
                  aria-label="Primary Address"
                  autoComplete="address-line1"
                  maxLength={256}
                />
              </Column>
            </Row>

            <Row mt="8">
              <Column width={6}>
                <Input
                  {...formBag.register(cityFieldName)}
                  type="text"
                  placeholder="City"
                  formVariant={get(errors, cityFieldName) ? 'error' : undefined}
                  helperText={get(errors, cityFieldName)?.message}
                  aria-label="City"
                  autoComplete="address-level2"
                  maxLength={256}
                />
              </Column>

              <Column width={3}>
                <Controller
                  control={formBag.control}
                  name={stateFieldName}
                  render={({ field }) => (
                    <Select
                      innerRef={field.ref}
                      value={ABBREVIATED_STATES_US.find(({ value }) => value === field.value)}
                      onChange={opt => {
                        if (opt) {
                          formBag.setValue(stateFieldName, opt.value, { shouldValidate: true });
                        }
                      }}
                      options={ABBREVIATED_STATES_US}
                      placeholder="State"
                      formVariant={get(errors, stateFieldName) ? 'error' : undefined}
                      helperText={get(errors, stateFieldName)?.message}
                      aria-label="State"
                    />
                  )}
                />
              </Column>

              <Column width={3}>
                <InputMask
                  {...formBag.register(postalCodeFieldName)}
                  type="text"
                  mask="99999"
                  placeholder="Zip code"
                  formVariant={get(errors, postalCodeFieldName) ? 'error' : undefined}
                  helperText={get(errors, postalCodeFieldName)?.message}
                  autoComplete="postal-code"
                  aria-label="Zip code"
                />
              </Column>
            </Row>
          </Grid>
        </>
      )}
    </Box>
  );
};
